import { useMemo } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { BoxedTabs } from 'app/shared-components/structure';
import { getClinicOrderStats } from 'app/services/clinic-order';
import { Roles } from 'app/auth';
import { ONE_MINUTE_IN_MS } from 'app/constants';

import { useClinicContext } from '../ClinicContext';
import useClinicMessageStats from '../hooks/useClinicMessageStats';

const tabsData = [
  {
    label: 'Unassigned',
    value: 'unassigned',
    badge: null,
    path: '/prescribing?private_prescription_filter=unassigned',
  },
  {
    label: 'Assigned',
    value: 'assigned',
    badge: null,
    path: '/prescribing?private_prescription_filter=assigned',
  },
  {
    label: 'Prescribed',
    value: 'prescribed',
    badge: null,
    path: '/prescribing?private_prescription_filter=prescribed',
  },
  {
    label: 'Ready',
    value: 'ready',
    badge: null,
    path: '/prescribing?private_prescription_filter=ready',
  },
  {
    label: 'Issue Reported',
    value: 'issue_reported',
    badge: null,
    path: '/prescribing?private_prescription_filter=issue_reported',
  },
  {
    label: 'Declined',
    value: 'declined',
    badge: null,
    path: '/prescribing?private_prescription_filter=declined',
  },
  {
    label: 'Archived',
    value: 'done',
    badge: null,
    path: '/prescribing?private_prescription_filter=done',
  },
];

const prescriberOnlyTabs = [
  {
    label: 'New messages',
    value: 'new_messages',
    badge: null,
    path: '/prescribing/new-messages',
  },
];

const pathByTabValue = [...tabsData, ...prescriberOnlyTabs]
  .filter((tab) => tab.path)
  .reduce((acc, tab) => {
    acc.set(tab.value, tab.path);
    return acc;
  }, new Map());

function OrderStateFilter() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { selectedClinicId } = useClinicContext();
  const { user } = useSelector((state) => state.auth);

  const isPrescriber = user?.roles?.includes(Roles.PRESCRIBER);

  const selectedTab = useMemo(() => {
    if (location.pathname.endsWith('/new-messages')) return 'new_messages';
    return searchParams.get('private_prescription_filter') || 'unassigned';
  }, [location.pathname, searchParams]);

  function handleTabChange(tabValue) {
    if (!pathByTabValue.has(tabValue)) throw new Error(`No path for tab value: ${tabValue}`);

    navigate(pathByTabValue.get(tabValue), { replace: true });
  }

  const { data: clinicOrderStats } = useQuery({
    queryKey: ['clinicOrderStats', selectedClinicId],
    queryFn: () => getClinicOrderStats({ clinic_id: selectedClinicId }),
    enabled: Boolean(selectedClinicId),
    refetchInterval: ONE_MINUTE_IN_MS,
  });

  const { data: unreadClinicMessageStats } = useClinicMessageStats(selectedClinicId);

  const tabs = useMemo(() => {
    const tabsWithBadges = isPrescriber ? [...tabsData, ...prescriberOnlyTabs] : tabsData;
    if (!clinicOrderStats && !unreadClinicMessageStats) return tabsWithBadges;

    return tabsWithBadges.map((tab) => {
      if (tab.value !== 'new_messages' && clinicOrderStats) {
        return { ...tab, badge: clinicOrderStats[tab.value] };
      }

      return { ...tab, badge: unreadClinicMessageStats?.unreadMessagesCount ?? 0 };
    });
  }, [clinicOrderStats, unreadClinicMessageStats, isPrescriber]);

  return <BoxedTabs tabs={tabs} selectedTab={selectedTab} handleChange={handleTabChange} />;
}

export default OrderStateFilter;
